import { CommandBar } from 'office-ui-fabric-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { allHearingsSelector } from '../../../features/apiFeatures/hearingSlice';
import {
  allHearingCreatorsSelector,
  hearingOwnerByHearingId,
} from '../../../features/apiFeatures/userSlice';
import { getEntityId } from '../../../features/helpers/apiHelpers';
import {
  changeHearingOwnerInitialize,
  isChangingHearingOwnerSelector,
  isPanelOpenSelector,
  selectedHearingIdSelector,
  setIsPanelOpen,
  setSelectedHearingId,
  isDeletingHearingSelector,
  forceDeleteHearing,
} from '../../../features/pageFeatures/administrationPageSlice';
import { initializeState } from '../../../features/pageFeatures/administrationPageSlice';
import { attributeOptionsMapper } from '../../../utilities/mapperFunctions';
import { getCommandBarItems } from './administrationPage/getCommandBarItems';
import { PeoplePickerPanel } from './PeoplePickerPanel';
import { HearingOverview } from './HearingOverview';
import { ConfirmationPrompt } from '../../../components/ConfirmationPrompt';
import { currentUserIsAdministratorSelector } from '../../../features/authenticationSlice';
import { Pages } from '../../../constants/pages';

const commandBarStyles = {
  root: {
    backgroundColor: '#FAFAFA',
    paddingRight: '0px',
  },
};

const AdministrationPage = () => {
  const { t: translator } = useTranslation();
  const dispatch = useDispatch();

  useEffect(() => {
    return function cleanup() {
      dispatch(initializeState());
    };
  }, [dispatch]);

  const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);

  const selectedHearingId = useSelector(selectedHearingIdSelector);
  const hearingOwner = useSelector(hearingOwnerByHearingId(selectedHearingId));
  const users = useSelector(allHearingCreatorsSelector);
  const isChangingHearingOwner = useSelector(isChangingHearingOwnerSelector);
  const isPanelOpen = useSelector(isPanelOpenSelector);
  const canChangeHearingOwner = !!selectedHearingId && !isChangingHearingOwner;

  const onPanelOpen = () => dispatch(setIsPanelOpen(true));
  const onPanelClose = () =>
    !isChangingHearingOwner && dispatch(setIsPanelOpen(false));
  const onChangeHearingOwner = (userId) =>
    userId &&
    selectedHearingId &&
    dispatch(
      changeHearingOwnerInitialize({ hearingId: selectedHearingId, userId }),
    );

  const isAdministrator = useSelector(currentUserIsAdministratorSelector);
  const isDeletingHearing = useSelector(isDeletingHearingSelector);
  const canDeleteHearing =
    !!selectedHearingId && isAdministrator && !isDeletingHearing;

  const userItems = users.map(attributeOptionsMapper('employeeDisplayName'));

  const onDeleteHearing = () => setIsDeletePopupOpen(true);
  const deleteHearingCallback = () => {
    selectedHearingId && dispatch(forceDeleteHearing(selectedHearingId));
    setIsDeletePopupOpen(false);
  };

  const commandBarItems = getCommandBarItems(
    translator,
    onPanelOpen,
    onDeleteHearing,
    canChangeHearingOwner,
    canDeleteHearing,
  );

  return (
    <div className="admin-page">
      <div className="admin-page-commandBar">
        <CommandBar items={commandBarItems} styles={commandBarStyles} />
      </div>
      <ConfirmationPrompt
        isOpen={isDeletePopupOpen}
        onDismiss={() => setIsDeletePopupOpen(false)}
        onConfirm={deleteHearingCallback}
        text={translator('pages.administration.deleteHearingPrompt')}
      />
      <HearingOverview
        headerText={translator('pages.administration.pageHeader')}
        hearingsSelector={allHearingsSelector}
        onHearingSelect={(hearingId) =>
          dispatch(setSelectedHearingId(hearingId))
        }
        onHearingClick={(hearingItem) =>
          dispatch(setSelectedHearingId(hearingItem.key))
        }
        activePage={Pages.AdministrationPage}
      />
      <PeoplePickerPanel
        onClick={onChangeHearingOwner}
        isOpen={isPanelOpen}
        onClose={onPanelClose}
        userItems={userItems}
        currentUserId={getEntityId(hearingOwner)}
        showSpinner={isChangingHearingOwner}
        headerText={translator('pages.administration.changeOwner')}
        guideText={translator('pages.administration.changeOwnerGuide')}
      />
    </div>
  );
};

export { AdministrationPage };
